<template>
  <div class="content-wrapper">
    <section class="content-header">
      <h1>
        BIN
      </h1>
      <ol class="breadcrumb">
        <li>
          <a @click="$router.push({ name: 'dashboard' })">
            <i class="fa fa-dashboard"></i> Dashboard
          </a>
        </li>
        <li>
          <a @click="$router.push({ name: 'bin-index' })">
            <i class="fa fa-bank"></i> BIN
          </a>
        </li>
      </ol>
    </section>

    <section class="content">
      <div class="box">
        <div class="box-body">
          <div class="box-header">
            <div class="box-title">
              <h3>BIN</h3>
            </div>
            <a class="btn btn-success pull-right"
              @click.prevent="add">
              Create New
            </a>
          </div>

          <div class="box-body table-responsive">
            <table class="table table-hover">
              <transition name="fade" mode="out-in">
                <tbody v-if="isLoading" key="loading">
                  <tr>
                    <td>
                      <h3>Loading</h3>
                    </td>
                  </tr>
                </tbody>

                <tbody v-else-if="!isLoading && items.length < 1" key="empty">
                  <tr>
                    <td>
                      <h3>Empty</h3>
                    </td>
                  </tr>
                </tbody>

                <tbody v-else key="data">
                  <tr>
                    <th>BIN</th>
                    <th>Bank Name</th>
                    <th>Card Series</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th class="text-center" style="width: 10rem">Action</th>
                  </tr>

                  <tr v-for="(item, index) in items" :key="index">
                    <td>{{ item.bin }}</td>
                    <td>{{ item.bank_name }}</td>
                    <td>{{ item.card_series }}</td>
                    <td>{{ getCreditCardName(item.category) }}</td>
                    <td>{{ item.type }}</td>
                    <td>
                      <strong :class="{
                        'text-success': item.is_enabled === 1,
                        'text-muted': item.is_enabled === 0
                      }">
                        <i class="fa fa-check-square-o" v-if="item.is_enabled === 1"></i>
                        <i class="fa fa-square-o" v-else></i>
                        {{ parseStatus(item.is_enabled) }}
                      </strong>
                    </td>
                    <td>
                      <a class="btn btn-primary btn-xs"
                        @click.prevent="edit(item.id)">
                        Edit
                      </a>
                      <a class="btn btn-danger btn-xs"
                        @click.prevent="remove(item)">
                        Delete
                      </a>
                    </td>
                  </tr>
                </tbody>
              </transition>
            </table>
          </div>

          <div class="box-footer">
            <Pagination
              :pagination="pagination"
              @goto-page="changePage" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Common from '@/mixins/Common';
import Pagination from '@/components/modules/common/Pagination';

export default {
  name: 'BinIndex',

  mixins: [Common],

  data() {
    return {
      isLoading: true,
      items: [],
      pagination: {},
      lists: {
        category: [],
      },
    };
  },

  components: {
    Pagination,
  },

  methods: {
    getCreditCards() {
      this.axios.get('/payments/creditcard')
        .then((response) => {
          const { data } = response.data;
          data.forEach((item) => {
            this.lists.category.push({
              label: item.name,
              value: item.id,
            });
          });
        })
        .catch((error) => {
          console.log(error);
          this.$swal({
            title: 'Failed',
            text: 'Failed to fetch available cards',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#666',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Try again',
          }).then((result) => {
            if (result.value) {
              this.getCreditCards();
            }
          });
        });
    },

    getCreditCardName(cardId) {
      const card = this.lists.category.find(item => item.value === cardId);
      return card.label;
    },

    fetch(page) {
      const pageQuery = this.$route.query.page;

      const params = {
        page: page || pageQuery,
      };

      this.isLoading = true;

      this.axios.get('/payments/bin', { params })
        .then((response) => {
          const { data, meta } = response.data;

          if (!data && (meta.pagination.current_page > meta.pagination.total_pages)) {
            return;
          }

          this.items = data;
          this.pagination = meta.pagination;
          this.isLoading = false;
        })
        .catch((error) => {
          console.log('Failed to fetch BINs', error);
          this.$swal({
            title: 'Fetch Failed',
            text: 'Failed to fetch BIN data',
            type: 'warning',
            timer: 3000,
          });
        });

      if (page) {
        window.history.pushState(`Payment BIN page: ${page}`, null, `/operation/bin?page=${page}`);
      }
    },

    changePage(page) {
      this.fetch(page);
    },

    parseStatus(status) {
      if (status === 1) return 'Active';
      return 'Inactive';
    },

    add() {
      this.$router.push({ name: 'bin-create' });
    },

    remove(bin) {
      this.$swal({
        title: 'Deleting BIN',
        text: `Are you sure you want to delete ${bin.bin} - ${bin.bank_name}?`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it',
      })
      .then((result) => {
        if (result.value) {
          this.delete(bin);
        } else {
          this.$swal(
            'Cancelled',
            null,
            'info',
          );
        }

        if (result.dismiss === 'overlay' || result.dismiss === 'cancel') {
          this.$swal(
            'Cancelled',
            null,
            'info',
          );
        }
      });
    },

    delete(bin) {
      this.axios.delete(`/payments/bin/${bin.id}`)
        .then(() => {
          this.fetch();

          this.$swal(
            'Deleted!',
            `${bin.name} has been deleted.`,
            'success',
          );
        });
    },

    edit(id) {
      this.$router.push({
        name: 'bin-edit',
        params: { id },
      });
    },
  },

  created() {
    this.getCreditCards();
    this.fetch();
  },
};
</script>

<style lang="scss">
  .label {
    margin: 0 0.125rem;
  }
</style>



// WEBPACK FOOTER //
// src/components/modules/operation/bin/Index.vue